import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Stack, Chip, IconButton, Tooltip,
  Autocomplete, TextField, Button, CircularProgress, Skeleton,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import ScienceIcon from '@mui/icons-material/Science';
import { getPendingDicomStudies, assignPendingDicomStudy, getPatients, downloadPatientStudy } from '../services/api';
import { useNotification } from '../hooks/useNotification';

// Estudios que llegaron por ingesta DICOM automática (feature dicom_studies)
// sin poder matchear un paciente por nombre — el matching es deliberadamente
// conservador (ver routers/dicom_ingest.py::_match_patient), así que esta
// pantalla es la vía de escape manual para esos casos.
const PendingRow = ({ study, onAssigned }) => {
  const notify = useNotification();
  const [patientOptions, setPatientOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [assigning, setAssigning] = useState(false);

  const handleSearch = useCallback(async (query) => {
    if (!query || query.trim().length < 2) { setPatientOptions([]); return; }
    setSearching(true);
    try {
      const res = await getPatients({ search: query.trim(), limit: 10 });
      setPatientOptions(res.data?.items || []);
    } catch { setPatientOptions([]); }
    finally { setSearching(false); }
  }, []);

  const handleAssign = async () => {
    if (!selectedPatient) return;
    setAssigning(true);
    try {
      await assignPendingDicomStudy(study.id, selectedPatient.id);
      notify(`Estudio asignado a ${selectedPatient.name}`, 'success');
      onAssigned(study.id);
    } catch (err) {
      notify(err.userMessage || 'No se pudo asignar el estudio', 'error');
    } finally {
      setAssigning(false);
    }
  };

  const handleDownload = async () => {
    try {
      const res = await downloadPatientStudy(study.id);
      const url = URL.createObjectURL(new Blob([res.data], { type: study.mime_type || 'application/dicom' }));
      const a = document.createElement('a'); a.href = url; a.download = study.original_filename; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      notify(err.userMessage || 'No se pudo descargar el archivo', 'error');
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 36, height: 36, borderRadius: 1.5, bgcolor: 'primary.50', color: 'primary.main', flexShrink: 0,
        }}>
          <ScienceIcon fontSize="small" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap>{study.original_filename}</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip label={study.category} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
            <Typography variant="caption" color="text.secondary">
              {new Date(study.created_at + 'Z').toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' })}
            </Typography>
          </Stack>
        </Box>
        <Autocomplete
          size="small"
          sx={{ width: { xs: '100%', sm: 260 } }}
          options={patientOptions}
          loading={searching}
          getOptionLabel={(p) => p.name || ''}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          value={selectedPatient}
          onChange={(_, val) => setSelectedPatient(val)}
          onInputChange={(_, val) => handleSearch(val)}
          renderInput={(params) => (
            <TextField {...params} label="Buscar paciente"
              InputProps={{
                ...params.InputProps,
                endAdornment: (<>{searching ? <CircularProgress size={14} /> : null}{params.InputProps.endAdornment}</>),
              }}
            />
          )}
        />
        <Button
          variant="contained" size="small" startIcon={<AssignmentIndIcon fontSize="small" />}
          onClick={handleAssign} disabled={!selectedPatient || assigning}
        >
          {assigning ? <CircularProgress size={14} /> : 'Asignar'}
        </Button>
        <Tooltip title="Descargar archivo original">
          <IconButton size="small" onClick={handleDownload}><DownloadIcon fontSize="small" /></IconButton>
        </Tooltip>
      </Stack>
    </Paper>
  );
};

const DicomPendientes = () => {
  const notify = useNotification();
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPendingDicomStudies();
      setStudies(res.data || []);
    } catch (err) {
      notify(err.userMessage || 'Error al cargar los estudios pendientes', 'error');
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  const handleAssigned = (studyId) => {
    setStudies(prev => prev.filter(s => s.id !== studyId));
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h5" component="h1" fontWeight={600} sx={{ mb: 0.5 }}>
        Estudios sin asignar
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Estudios recibidos automáticamente (DICOM) donde no se pudo identificar el paciente por nombre —
        buscá y asigná el paciente correcto para que pase a su Historia Clínica.
      </Typography>

      {loading ? (
        <Stack spacing={1.5}>
          <Skeleton variant="rounded" height={72} />
          <Skeleton variant="rounded" height={72} />
        </Stack>
      ) : studies.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Typography color="text.secondary">No hay estudios pendientes de asignar.</Typography>
        </Paper>
      ) : (
        <Stack spacing={1.5}>
          {studies.map(s => <PendingRow key={s.id} study={s} onAssigned={handleAssigned} />)}
        </Stack>
      )}
    </Box>
  );
};

export default DicomPendientes;
